import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const ContentEngine = () => {
  const [mode, setMode] = useState('smart');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(0);
  const [showPsychology, setShowPsychology] = useState(false);
  const [userPatterns, setUserPatterns] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedContent, setSavedContent] = useState([]);
  const [showSavedContent, setShowSavedContent] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [librarySize, setLibrarySize] = useState(0);
  const [frameworksExplored, setFrameworksExplored] = useState(0);
  
  const [expandedSavedId, setExpandedSavedId] = useState(null);
  const [selectedVariationsToSave, setSelectedVariationsToSave] = useState([]);
  
  const [userIntent, setUserIntent] = useState('');
  const [quickContext, setQuickContext] = useState({
    business: '',
    audience: '',
    tone: 'conversational'
  });
  
  const [selectedContentType, setSelectedContentType] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [specificFormData, setSpecificFormData] = useState({
    business_context: {},
    target_audience: '',
    goal: '',
    tone: 'conversational',
    additional_context: ''
  });
  
  const [existingContent, setExistingContent] = useState('');
  const [contentPurpose, setContentPurpose] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  
  const { token } = useAuth();
  const [availableOptions, setAvailableOptions] = useState(null);

  const fetchUserStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content-engine/user-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setWeeklyCount(data.stats.this_week || 0);
        setLibrarySize(data.stats.saved_content || 0);
        setFrameworksExplored(data.stats.frameworks_explored || 0);
      }
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
    }
  };

  const fetchSavedContent = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content-engine/saved-content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSavedContent(data.data);
      }
    } catch (error) {
      logger.error('Failed to fetch saved content:', error);
    }
  };

  const handleSaveContent = () => {
    if (!generatedContent?.variations) return;
    setSelectedVariationsToSave(generatedContent.variations.map((_, i) => i));
    setShowSaveModal(true);
  };

  const deleteSavedContent = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content-engine/saved-content/${id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSavedContent(prev => prev.filter(item => item.id !== id));
        setDeleteConfirm(null);
        fetchUserStats();
      } else {
        alert(`Failed to delete content: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      logger.error('❌ Failed to delete:', error);
      alert(`Failed to delete content: ${error.message}`);
    }
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setGeneratedContent(null);
    setAnalysisResult(null);
    setSelectedVariation(0);
    setShowPsychology(false);
    setSelectedPlatforms([]);
  };
  
  useEffect(() => {
    fetchAvailableOptions();
    fetchUserPatterns();
    fetchUserStats();
    fetchSavedContent();
  }, []);

  useEffect(() => {
    fetchUserStats();
  }, [mode]);
  
  const fetchAvailableOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content-engine/available-options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      logger.info('Available options:', data);
      setAvailableOptions(data);
    } catch (error) {
      logger.error('Failed to fetch options:', error);
      setAvailableOptions({
        tones: ['conversational', 'professional', 'casual', 'urgent', 'friendly', 'authoritative', 'empathetic', 'bold', 'humorous'],
        content_types: [],
        platforms: []
      });
    }
  };
  
  const fetchUserPatterns = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content-engine/my-patterns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUserPatterns(data);
    } catch (error) {
      logger.error('Failed to fetch patterns:', error);
    }
  };
  
 const generateSmartContent = async () => {
  setLoading(true);
  setAnalysisResult(null);
  try {
    const response = await fetch(`${BACKEND_URL}/api/content-engine/smart-generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        user_intent: userIntent,
        quick_context: quickContext
      })
    });
    
    const result = await response.json();
    logger.info('✅ Smart generation result:', result);
    
    if (result.success) {
      setGeneratedContent(result);
      setSelectedVariation(0);
      
      // Wait a bit for DB write to complete, then refresh stats
      setTimeout(() => {
        fetchUserStats();
      }, 500);
    }
  } catch (error) {
    logger.error('Generation failed:', error);
  } finally {
    setLoading(false);
  }
};

const generateSpecificContent = async () => {
  setLoading(true);
  setAnalysisResult(null);
  try {
    const response = await fetch(`${BACKEND_URL}/api/content-engine/specific-generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        content_type: selectedContentType,
        platforms: selectedPlatforms.length > 0 ? selectedPlatforms : null,
        business_context: specificFormData.business_context,
        target_audience: specificFormData.target_audience,
        goal: specificFormData.goal,
        tone: specificFormData.tone,
        additional_context: specificFormData.additional_context,
        variations_count: 3
      })
    });
    
    const result = await response.json();
    logger.info('✅ Specific generation result:', result);
    
    if (result.success) {
      setGeneratedContent(result);
      setSelectedVariation(0);
      
      // Wait a bit for DB write to complete, then refresh stats
      setTimeout(() => {
        fetchUserStats();
      }, 500);
    } else {
      logger.error('Generation failed:', result);
    }
  } catch (error) {
    logger.error('Generation failed:', error);
  } finally {
    setLoading(false);
  }
};

const analyzeContent = async () => {
  setLoading(true);
  setGeneratedContent(null);
  try {
    const response = await fetch(`${BACKEND_URL}/api/content-engine/analyze-improve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        content: existingContent,
        content_purpose: contentPurpose,
        business_context: {
          product: quickContext.business,
          industry: 'General'
        },
        target_audience: quickContext.audience || 'General audience',
        goal: 'improve_conversion',
        improvement_focus: 'overall',
        variations_count: 2
      })
    });
    
    const result = await response.json();
    logger.info('✅ Analysis result:', result);
    
    if (result.success) {
      setAnalysisResult(result.analysis);
      
      const mappedVariations = result.improved_versions.map((version, index) => ({
        version: version.version || index + 1,
        content: version.content || {
          full_text: version.improved_content || ''
        },
        psychological_mechanics: {
          primary_framework: version.frameworks_applied?.[0] || 'Mixed approach',
          emotional_journey: 'Progressive engagement',
          triggers_activated: version.emotional_triggers_added || [],
          platform_psychology: version.why_better || 'Platform-optimized version'
        },
        platform_optimization: {
          platform_features_used: version.platform_optimizations || [],
          native_feel_score: 90
        },
        predicted_metrics: {
          conversion_rate: version.predicted_score || 0,
          engagement_rate: Math.min(100, (version.predicted_score || 0) + 5),
          persuasion_score: Math.min(100, (version.predicted_score || 0) + 3),
          platform_performance: Math.min(100, (version.predicted_score || 0) + 2)
        },
        score_improvement: version.score_improvement || 0
      }));
      
      setGeneratedContent({
        variations: mappedVariations,
        content_type: result.detected_type?.type || 'analyzed_content',
        platforms: [result.detected_type?.platform || 'general']
      });
      setSelectedVariation(0);
      
      // Wait a bit for DB write to complete, then refresh stats
      setTimeout(() => {
        fetchUserStats();
      }, 500);
    }
  } catch (error) {
    logger.error('Analysis failed:', error);
  } finally {
    setLoading(false);
  }
};

  const DeleteConfirmModal = ({ item, onConfirm, onCancel }) => {
    if (!item) return null;
    
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ isolation: 'isolate' }}>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-md" 
          onClick={onCancel} 
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/20 shadow-2xl shadow-red-500/10"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/20 border-2 border-red-500/30 flex items-center justify-center"
          >
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </motion.div>

          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-3">Delete Content?</h3>
            <p className="text-slate-300 mb-4">
              Are you sure you want to delete <span className="font-semibold text-white">"{item.name}"</span>?
            </p>
            <p className="text-sm text-slate-400">
              This action cannot be undone. The content will be permanently removed from your library.
            </p>
          </div>

          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-slate-700/50 hover:bg-slate-700 text-white rounded-xl font-medium transition-all border border-slate-600"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onConfirm(item.id)}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-medium transition-all shadow-lg shadow-red-600/25 hover:shadow-red-600/40"
            >
              Delete
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  };

  const SaveModal = ({ show, onClose }) => {
    const [name, setName] = useState('');
    const [tags, setTags] = useState('');
    const [saving, setSaving] = useState(false);
    
    if (!show || !generatedContent) return null;
    
   const handleSave = async () => {
  setSaving(true);
  try {
    for (const varIndex of selectedVariationsToSave) {
      const variation = generatedContent.variations[varIndex];
      const contentText = variation.content?.full_text || 
                         JSON.stringify(variation.content, null, 2) || 
                         '';
      
      const response = await fetch(`${BACKEND_URL}/api/content-engine/save-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: `${name || 'Content'} - Version ${variation.version || varIndex + 1}`,
          content: contentText,
          content_type: generatedContent?.content_type || 'general',
          platform: variation.platform || generatedContent?.platforms?.[0] || 'general',
          tags: tags.split(',').map(t => t.trim()).filter(t => t),
          variation_number: variation.version || varIndex + 1,
          predicted_metrics: variation.predicted_metrics || {},
          generation_mode: mode  // ✅ ADD THIS LINE
        })
      });
      
      if (!response.ok) throw new Error('Save failed');
    }
    
    fetchSavedContent();
    fetchUserStats();
    onClose();
    setName('');
    setTags('');
    setSelectedVariationsToSave([]);
  } catch (error) {
    logger.error('Save failed:', error);
    alert('Failed to save content. Please try again.');
  } finally {
    setSaving(false);
  }
};
    
    const toggleVariation = (index) => {
      setSelectedVariationsToSave(prev => 
        prev.includes(index) 
          ? prev.filter(i => i !== index)
          : [...prev, index]
      );
    };
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-slate-800/90 backdrop-blur-xl rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-slate-700/50 shadow-2xl"
        >
          <h3 className="text-xl font-semibold text-white mb-4">Save Content Variations</h3>
          
          <div className="space-y-3 mb-6">
            <label className="text-sm font-medium text-slate-300 block">
              Select variations to save:
            </label>
            {generatedContent.variations.map((variation, index) => {
              const content = variation.content?.full_text || 
                             JSON.stringify(variation.content, null, 2) || 
                             '';
              const preview = content.substring(0, 150) + (content.length > 150 ? '...' : '');
              
              return (
                <div
                  key={index}
                  onClick={() => toggleVariation(index)}
                  className={`
                    p-4 rounded-lg border cursor-pointer transition-all
                    ${selectedVariationsToSave.includes(index)
                      ? 'bg-blue-600/20 border-blue-500/50'
                      : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedVariationsToSave.includes(index)}
                      onChange={() => toggleVariation(index)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-white">
                          Version {variation.version || index + 1}
                        </span>
                        {variation.platform_name && (
                          <span className="text-xs px-2 py-0.5 bg-purple-600/20 border border-purple-600/30 rounded-full text-purple-300">
                            {variation.platform_name}
                          </span>
                        )}
                        {variation.predicted_metrics && (
                          <span className="text-xs text-green-400">
                            {variation.predicted_metrics.conversion_rate}% predicted
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-300">{preview}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Name your content
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Homepage Hero Copy"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                autoFocus
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g., landing page, conversion, test"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
              />
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || selectedVariationsToSave.length === 0}
              className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-medium shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-all disabled:opacity-50"
            >
              {saving ? 'Saving...' : `Save ${selectedVariationsToSave.length} Variation${selectedVariationsToSave.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

const SavedContentModal = ({ show, onClose, savedContent, onDelete }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'smart', 'specific', 'analyze'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'type', 'mode'
  
  if (!show) return null;

  const toggleExpand = (id) => {
    setExpandedSavedId(expandedSavedId === id ? null : id);
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const filtered = getFilteredContent();
    if (selectedItems.length === filtered.length && filtered.length > 0) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filtered.map(item => item.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      for (const id of selectedItems) {
        await onDelete(id);
      }
      setSelectedItems([]);
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      logger.error('Bulk delete failed:', error);
      alert('Failed to delete some items');
    }
  };

  // 🎨 BEAUTIFUL CONTENT RENDERER - Enhanced for all structures
  const renderSavedContent = (item) => {
    const content = item.content;
    
    // Handle legacy string content
    if (typeof content === 'string') {
      return (
        <div className="text-base text-slate-200 leading-relaxed whitespace-pre-wrap font-sans">
          {content}
        </div>
      );
    }
    
    // Handle object content
    if (!content || typeof content !== 'object') {
      return (
        <div className="text-slate-400 italic">
          No content available
        </div>
      );
    }
    
    // CASE 1: Email Structure (subject_line, preview_text, body, ps)
    if (content.subject_line) {
      return (
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <div className="text-xs font-medium text-blue-400 mb-1">📧 SUBJECT LINE</div>
            <div className="text-white font-semibold text-sm md:text-base">{content.subject_line}</div>
          </div>
          
          {content.preview_text && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-cyan-400 mb-1">👁️ PREVIEW TEXT</div>
              <div className="text-slate-300 text-sm">{content.preview_text}</div>
            </div>
          )}
          
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <div className="text-xs font-medium text-green-400 mb-2">✉️ EMAIL BODY</div>
            <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
              {content.body}
            </div>
          </div>
          
          {content.ps && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-purple-400 mb-1">📝 P.S.</div>
              <div className="text-slate-300 text-sm italic">{content.ps}</div>
            </div>
          )}
        </div>
      );
    }
    
    // CASE 2: Slide-based content (carousel, stories)
    if (content.slides && Array.isArray(content.slides)) {
      return (
        <div className="space-y-3">
          {content.cover_slide && (
            <div className="bg-gradient-to-br from-purple-600/10 to-pink-600/10 rounded-lg p-3 border border-purple-500/30 mb-3">
              <div className="text-xs font-medium text-purple-400 mb-1">🎯 COVER SLIDE</div>
              <div className="text-white font-bold">{content.cover_slide}</div>
            </div>
          )}
          
          {content.slides.map((slide, index) => (
            <div key={index} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-purple-400">
                  📄 SLIDE {slide.slide_number || index + 1}
                </div>
                {slide.visual_note && (
                  <div className="text-xs text-slate-500">🎨 {slide.visual_note}</div>
                )}
              </div>
              {slide.headline && (
                <div className="text-white font-bold mb-2 text-sm md:text-base">{slide.headline}</div>
              )}
              {slide.body && (
                <div className="text-slate-300 text-sm">{slide.body}</div>
              )}
            </div>
          ))}
          
          {content.cta_slide && (
            <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 rounded-lg p-3 border border-green-500/30">
              <div className="text-xs font-medium text-green-400 mb-1">🎯 CTA SLIDE</div>
              <div className="text-white font-semibold">{content.cta_slide}</div>
            </div>
          )}
        </div>
      );
    }
    
    // CASE 3: Long-form with headline/sections (sales page, landing page)
    if (content.headline || content.body_sections) {
      return (
        <div className="space-y-3">
          {content.headline && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-yellow-400 mb-1">🎯 HEADLINE</div>
              <div className="text-white text-lg md:text-xl font-bold">{content.headline}</div>
            </div>
          )}
          
          {content.subheadline && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-blue-400 mb-1">📝 SUBHEADLINE</div>
              <div className="text-slate-300 text-base md:text-lg">{content.subheadline}</div>
            </div>
          )}
          
          {content.body_sections && typeof content.body_sections === 'object' && (
            <div className="space-y-2">
              {Object.entries(content.body_sections).map(([key, value]) => value && (
                <div key={key} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs font-medium text-cyan-400 mb-2 uppercase">
                    {key.replace(/_/g, ' ')}
                  </div>
                  <div className="text-slate-200 text-sm whitespace-pre-wrap">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {content.full_text && !content.body_sections && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                {content.full_text}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // CASE 4: Video/Audio Script with timestamps
    if (content.timestamps || content.hook_visual || content.visual_directions) {
      return (
        <div className="space-y-3">
          {content.hook_visual && (
            <div className="bg-gradient-to-br from-orange-600/10 to-red-600/10 rounded-lg p-3 border border-orange-500/30">
              <div className="text-xs font-medium text-orange-400 mb-1">🎬 HOOK VISUAL</div>
              <div className="text-white font-semibold text-sm">{content.hook_visual}</div>
            </div>
          )}
          
          {content.full_text && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-green-400 mb-2">📜 SCRIPT</div>
              <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                {content.full_text}
              </div>
            </div>
          )}
          
          {content.timestamps && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-blue-400 mb-2">⏱️ TIMESTAMPS</div>
              <div className="text-slate-300 text-sm whitespace-pre-wrap">
                {content.timestamps}
              </div>
            </div>
          )}
          
          {content.visual_directions && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-purple-400 mb-2">🎥 VISUAL DIRECTIONS</div>
              <div className="text-slate-300 text-sm whitespace-pre-wrap">
                {content.visual_directions}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // CASE 5: Ad Copy (headlines, descriptions, CTA)
    if (content.primary_text || content.headlines || content.description) {
      return (
        <div className="space-y-3">
          {content.headlines && Array.isArray(content.headlines) && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-yellow-400 mb-2">✨ HEADLINES</div>
              {content.headlines.map((headline, i) => (
                <div key={i} className="text-white font-semibold text-sm mb-1">
                  {i + 1}. {headline}
                </div>
              ))}
            </div>
          )}
          
          {content.primary_text && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-blue-400 mb-2">📝 PRIMARY TEXT</div>
              <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                {content.primary_text}
              </div>
            </div>
          )}
          
          {content.headline && !content.headlines && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-yellow-400 mb-1">✨ HEADLINE</div>
              <div className="text-white font-semibold text-sm">{content.headline}</div>
            </div>
          )}
          
          {content.description && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="text-xs font-medium text-cyan-400 mb-1">📄 DESCRIPTION</div>
              <div className="text-slate-300 text-sm">{content.description}</div>
            </div>
          )}
          
          {content.cta_button && (
            <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 rounded-lg p-3 border border-green-500/30">
              <div className="text-xs font-medium text-green-400 mb-1">🎯 CTA BUTTON</div>
              <div className="text-white font-bold">{content.cta_button}</div>
            </div>
          )}
        </div>
      );
    }
    
    // CASE 6: Simple full_text (social posts, most content)
    if (content.full_text) {
      return (
        <div className="text-base text-slate-200 leading-relaxed whitespace-pre-wrap font-sans">
          {content.full_text}
        </div>
      );
    }
    
    // FALLBACK: Show JSON in a nicer way
    return (
      <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
        <div className="text-xs font-medium text-slate-400 mb-2">📋 RAW CONTENT</div>
        <pre className="text-xs text-slate-300 whitespace-pre-wrap overflow-x-auto">
          {JSON.stringify(content, null, 2)}
        </pre>
      </div>
    );
  };

  // Helper to get a clean preview for collapsed state
  const getContentPreview = (item) => {
    const content = item.content;
    
    if (typeof content === 'string') {
      return content.substring(0, 150) + (content.length > 150 ? '...' : '');
    }
    
    if (content?.subject_line) {
      return content.subject_line;
    }
    
    if (content?.headline) {
      return content.headline;
    }
    
    if (content?.primary_text) {
      return content.primary_text.substring(0, 150) + (content.primary_text.length > 150 ? '...' : '');
    }
    
    if (content?.full_text) {
      return content.full_text.substring(0, 150) + (content.full_text.length > 150 ? '...' : '');
    }
    
    if (content?.slides?.[0]) {
      return content.slides[0].headline || content.slides[0].body?.substring(0, 100) || 'Carousel content';
    }
    
    return 'View content...';
  };

  // 🆕 Detect generation mode from item metadata
  const getGenerationMode = (item) => {
    // Check if there's mode info in the item (we'll need to store this in backend)
    if (item.generation_mode) {
      return item.generation_mode;
    }
    
    // Fallback: Try to infer from content structure or metadata
    // If it has analysis data, it's from analyze mode
    if (item.analysis_result || item.improved_versions) {
      return 'analyze';
    }
    
    // If it has specific platform selection, it's specific mode
    if (item.platforms && item.platforms.length > 1) {
      return 'specific';
    }
    
    // Otherwise assume smart mode
    return 'smart';
  };

  const getModeIcon = (mode) => {
    switch(mode) {
      case 'smart': return '✨';
      case 'specific': return '🎯';
      case 'analyze': return '🔬';
      default: return '📝';
    }
  };

  const getModeBadge = (mode) => {
    const configs = {
      smart: { bg: 'bg-blue-600/20', border: 'border-blue-500/50', text: 'text-blue-300', label: 'Smart Mode' },
      specific: { bg: 'bg-cyan-600/20', border: 'border-cyan-500/50', text: 'text-cyan-300', label: 'Specific' },
      analyze: { bg: 'bg-teal-600/20', border: 'border-teal-500/50', text: 'text-teal-300', label: 'Analyzed' }
    };
    
    const config = configs[mode] || configs.smart;
    
    return (
      <span className={`text-xs px-2 py-0.5 ${config.bg} border ${config.border} rounded-full ${config.text} font-medium flex-shrink-0 flex items-center gap-1`}>
        <span>{getModeIcon(mode)}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  // 🆕 Filter and sort logic
  const getFilteredContent = () => {
    let filtered = [...savedContent];
    
    // Filter by mode
    if (filterMode !== 'all') {
      filtered = filtered.filter(item => getGenerationMode(item) === filterMode);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.content_type?.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        getContentPreview(item).toLowerCase().includes(query)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'type':
          return (a.content_type || '').localeCompare(b.content_type || '');
        case 'mode':
          return getGenerationMode(a).localeCompare(getGenerationMode(b));
        case 'date':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    return filtered;
  };

  const filteredContent = getFilteredContent();
  const isAllSelected = filteredContent.length > 0 && selectedItems.length === filteredContent.length;
  const isSomeSelected = selectedItems.length > 0 && selectedItems.length < filteredContent.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4" style={{ isolation: 'isolate' }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl p-3 md:p-6 w-full max-w-6xl max-h-[90vh] md:max-h-[85vh] overflow-hidden border border-slate-700/50 shadow-2xl flex flex-col"
      >
        {/* Header with Bulk Actions */}
        <div className="mb-4 md:mb-6 pb-3 md:pb-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl md:text-2xl font-semibold text-white">
              Saved Content ({filteredContent.length}{filteredContent.length !== savedContent.length ? ` of ${savedContent.length}` : ''})
            </h3>
            
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors text-xl md:text-2xl leading-none flex-shrink-0"
            >
              ✕
            </button>
          </div>
          
          {/* 🆕 Search and Filter Controls */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-3">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, type, tags, or content..."
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            
            {/* Mode Filter */}
            <div className="flex gap-2">
              {['all', 'smart', 'specific', 'analyze'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setFilterMode(mode)}
                  className={`
                    px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-all border whitespace-nowrap
                    ${filterMode === mode
                      ? 'bg-blue-600/20 border-blue-500/50 text-blue-300'
                      : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500'
                    }
                  `}
                >
                  {mode === 'all' ? '📚 All' : `${getModeIcon(mode)} ${mode.charAt(0).toUpperCase() + mode.slice(1)}`}
                </button>
              ))}
            </div>
            
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/50"
            >
              <option value="date">Sort: Date</option>
              <option value="name">Sort: Name</option>
              <option value="type">Sort: Type</option>
              <option value="mode">Sort: Mode</option>
            </select>
          </div>
          
          {/* Bulk Actions */}
          {savedContent.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={toggleSelectAll}
                className={`
                  px-2 md:px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-all border
                  ${isAllSelected || isSomeSelected
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-300'
                    : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500'
                  }
                `}
              >
                {isAllSelected ? '☑️ Deselect All' : isSomeSelected ? '☑️ Select All' : '☐ Select All'}
              </button>
              
              {selectedItems.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-slate-400">
                    {selectedItems.length} selected
                  </span>
                  <button
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="px-2 md:px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 rounded-lg text-xs md:text-sm text-red-300 transition-all font-medium"
                  >
                    🗑️ Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content List */}
        <div className="flex-1 overflow-y-auto pr-1 md:pr-2 -mr-1 md:-mr-2">
          {filteredContent.length === 0 ? (
            <div className="text-center py-8 md:py-12 text-slate-400">
              <p className="text-base md:text-lg">
                {searchQuery || filterMode !== 'all' ? 'No content matches your filters' : 'No saved content yet'}
              </p>
              <p className="text-xs md:text-sm mt-2">
                {searchQuery || filterMode !== 'all' ? 'Try adjusting your search or filters' : 'Generate some content and click "Save" to store it here'}
              </p>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {filteredContent.map((item) => {
                const isExpanded = expandedSavedId === item.id;
                const isSelected = selectedItems.includes(item.id);
                const preview = getContentPreview(item);
                const mode = getGenerationMode(item);
                
                return (
                  <div
                    key={item.id}
                    className={`
                      bg-slate-900/50 rounded-xl border overflow-hidden transition-all
                      ${isSelected 
                        ? 'border-blue-500/50 shadow-lg shadow-blue-500/10' 
                        : 'border-slate-700/50 hover:border-slate-600/50'
                      }
                    `}
                  >
                    <div className="p-3 md:p-5 bg-slate-800/30">
                      <div className="flex items-start justify-between gap-2 md:gap-4 mb-2 md:mb-3">
                        {/* Checkbox + Content Info */}
                        <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                          <button
                            onClick={() => toggleSelectItem(item.id)}
                            className={`
                              mt-0.5 md:mt-1 w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0
                              ${isSelected
                                ? 'bg-blue-600 border-blue-500'
                                : 'border-slate-600 hover:border-slate-500'
                              }
                            `}
                          >
                            {isSelected && (
                              <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2 flex-wrap">
                              <h4 className="font-semibold text-white text-sm md:text-lg break-words">{item.name}</h4>
                              {getModeBadge(mode)}
                              {item.variation_number && (
                                <span className="text-xs px-1.5 md:px-2 py-0.5 md:py-1 bg-purple-600/20 border border-purple-600/30 rounded-full text-purple-300 font-medium flex-shrink-0">
                                  V{item.variation_number}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-400 flex-wrap">
                              <span className="capitalize">{item.content_type?.replace(/_/g, ' ')}</span>
                              <span className="hidden md:inline">•</span>
                              <span>{new Date(item.created_at).toLocaleDateString()}</span>
                              {item.platform && (
                                <>
                                  <span className="hidden md:inline">•</span>
                                  <span className="capitalize">{item.platform}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-1 md:gap-2 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const textToCopy = typeof item.content === 'string'
                                ? item.content
                                : item.content?.full_text || JSON.stringify(item.content, null, 2);
                              navigator.clipboard.writeText(textToCopy);
                              const btn = e.currentTarget;
                              const originalText = btn.textContent;
                              btn.textContent = '✓';
                              setTimeout(() => { btn.textContent = originalText; }, 1000);
                            }}
                            className="px-2 md:px-4 py-1.5 md:py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/30 rounded-lg text-xs md:text-sm text-blue-300 transition-all font-medium"
                          >
                            Copy
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ id: item.id, name: item.name });
                            }}
                            className="px-2 md:px-4 py-1.5 md:py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 rounded-lg text-xs md:text-sm text-red-300 transition-all font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 md:gap-2 ml-6 md:ml-8">
                          {item.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="px-2 md:px-3 py-0.5 md:py-1 bg-blue-600/10 border border-blue-600/20 rounded-full text-xs text-blue-300"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3 md:p-5">
                      {isExpanded ? (
                        // 🎨 FULL BEAUTIFUL RENDERING
                        renderSavedContent(item)
                      ) : (
                        // Preview (collapsed state)
                        <div className="text-sm md:text-base text-slate-300 leading-relaxed line-clamp-3">
                          {preview}
                        </div>
                      )}
                      
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(item.id);
                        }}
                        className="mt-3 md:mt-4 text-xs md:text-sm text-blue-400 hover:text-blue-300 font-medium flex items-center gap-2 transition-colors"
                      >
                        {isExpanded ? (
                          <>
                            <span>▲</span>
                            <span>Show Less</span>
                          </>
                        ) : (
                          <>
                            <span>▼</span>
                            <span>Show More</span>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {isExpanded && item.predicted_metrics && (
                      <div className="px-3 md:px-5 pb-3 md:pb-5">
                        <div className="pt-3 md:pt-4 border-t border-slate-700/50">
                          <h5 className="text-xs font-medium text-slate-400 mb-2 md:mb-3 uppercase tracking-wider">Predicted Performance</h5>
                          <div className="grid grid-cols-3 gap-2 md:gap-3">
                            <div className="bg-slate-800/50 rounded-lg p-2 md:p-4 text-center border border-slate-700/30">
                              <div className="text-xl md:text-2xl font-bold text-green-400 mb-0.5 md:mb-1">
                                {item.predicted_metrics.conversion_rate || 'N/A'}%
                              </div>
                              <div className="text-xs text-slate-400">Conversion</div>
                            </div>
                            <div className="bg-slate-800/50 rounded-lg p-2 md:p-4 text-center border border-slate-700/30">
                              <div className="text-xl md:text-2xl font-bold text-blue-400 mb-0.5 md:mb-1">
                                {item.predicted_metrics.engagement_rate || 'N/A'}%
                              </div>
                              <div className="text-xs text-slate-400">Engagement</div>
                            </div>
                            <div className="bg-slate-800/50 rounded-lg p-2 md:p-4 text-center border border-slate-700/30">
                              <div className="text-xl md:text-2xl font-bold text-cyan-400 mb-0.5 md:mb-1">
                                {item.predicted_metrics.persuasion_score || 'N/A'}%
                              </div>
                              <div className="text-xs text-slate-400">Persuasion</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Bulk Delete Confirmation Modal */}
      <AnimatePresence>
        {showBulkDeleteConfirm && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-md" 
              onClick={() => setShowBulkDeleteConfirm(false)} 
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl p-6 md:p-8 max-w-md w-full border border-red-500/20 shadow-2xl shadow-red-500/10 mx-4"
            >
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 md:mb-6 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/20 border-2 border-red-500/30 flex items-center justify-center"
              >
                <svg className="w-6 h-6 md:w-8 md:h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </motion.div>

              <div className="text-center mb-6 md:mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">Delete Multiple Items?</h3>
                <p className="text-sm md:text-base text-slate-300 mb-3 md:mb-4">
                  You're about to delete <span className="font-bold text-red-400">{selectedItems.length}</span> item{selectedItems.length > 1 ? 's' : ''}.
                </p>
                <p className="text-xs md:text-sm text-slate-400">
                  This action cannot be undone. All selected content will be permanently removed.
                </p>
              </div>

              <div className="flex gap-2 md:gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  className="flex-1 py-2.5 md:py-3 px-3 md:px-4 bg-slate-700/50 hover:bg-slate-700 text-white rounded-xl font-medium transition-all border border-slate-600 text-sm md:text-base"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleBulkDelete}
                  className="flex-1 py-2.5 md:py-3 px-3 md:px-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-medium transition-all shadow-lg shadow-red-600/25 hover:shadow-red-600/40 text-sm md:text-base"
                >
                  Delete All
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <a
                href="https://app.whydiditdie.com/dashboard"
                className="px-3 py-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-all text-sm whitespace-nowrap"
              >

                ← Dashboard
              </a>
              <div className="flex-1 min-w-0 overflow-hidden">
                <h1 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  AI Content Engine
                </h1>
                <p className="text-slate-400 text-xs md:text-sm mt-0.5 md:mt-1 hidden sm:block">
                  Intelligent structure-aware content generation with multi-platform optimization
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm flex-shrink-0">
              <div className="flex flex-col sm:flex-row items-center px-2 md:px-3 py-1 md:py-1.5 bg-orange-600/20 border border-orange-600/30 rounded-lg text-orange-300 whitespace-nowrap">
                <span className="hidden sm:inline mr-1">🔥</span>
                <span className="font-semibold">{weeklyCount}</span>
                <span className="hidden md:inline ml-1 text-xs">this week</span>
              </div>
              <div className="flex flex-col sm:flex-row items-center px-2 md:px-3 py-1 md:py-1.5 bg-purple-600/20 border border-purple-600/30 rounded-lg text-purple-300 whitespace-nowrap">
                <span className="hidden sm:inline mr-1">💎</span>
                <span className="font-semibold">{librarySize}</span>
                <span className="hidden md:inline ml-1 text-xs">library</span>
              </div>
              <div className="flex flex-col sm:flex-row items-center px-2 md:px-3 py-1 md:py-1.5 bg-cyan-600/20 border border-cyan-600/30 rounded-lg text-cyan-300 whitespace-nowrap">
                <span className="hidden sm:inline mr-1">⚡</span>
                <span className="font-semibold">{frameworksExplored}</span>
                <span className="hidden md:inline ml-1 text-xs">explored</span>
              </div>
              <button
                onClick={() => setShowSavedContent(!showSavedContent)}
                className="px-2 md:px-3 py-1 md:py-1.5 bg-blue-600/20 border border-blue-600/30 rounded-lg text-blue-300 hover:bg-blue-600/30 transition-all whitespace-nowrap flex items-center gap-1"
              >
                <span>📚</span>
                <span className="hidden sm:inline">Saved</span>
                <span>({savedContent.length})</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mode Selector */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'smart', label: 'Smart Mode', desc: 'Just tell me what you need', icon: '✨' },
            { id: 'specific', label: 'Specific Content', desc: 'Choose exact type', icon: '🎯' },
            { id: 'analyze', label: 'Analyze & Improve', desc: 'Upgrade existing', icon: '🔬' }
          ].map(({ id, label, desc, icon }) => (
            <motion.button
              key={id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleModeChange(id)}
              className={`
                relative p-4 rounded-xl transition-all duration-300 border backdrop-blur-xl
                ${mode === id 
                  ? 'bg-gradient-to-br from-blue-600/20 to-cyan-700/20 border-blue-500/50 shadow-lg shadow-blue-500/20' 
                  : 'bg-slate-800/30 border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600'
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <span className="text-2xl">{icon}</span>
                <div className="text-center">
                  <div className="font-semibold text-white text-xs md:text-base">{label}</div>
                  <div className="text-xs text-slate-400 mt-1 hidden md:block">{desc}</div>
                </div>
              </div>
              {mode === id && (
                <motion.div
                  layoutId="activeMode"
                  className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent rounded-xl pointer-events-none"
                />
              )}
            </motion.button>
          ))}
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <AnimatePresence mode="wait">
          {mode === 'smart' && <SmartMode key="smart" {...{ userIntent, setUserIntent, quickContext, setQuickContext, generateSmartContent, loading }} />}
          {mode === 'specific' && <SpecificMode key="specific" {...{ selectedContentType, setSelectedContentType, selectedPlatforms, setSelectedPlatforms, specificFormData, setSpecificFormData, generateSpecificContent, loading, availableOptions }} />}
          {mode === 'analyze' && <AnalyzeMode key="analyze" {...{ existingContent, setExistingContent, analyzeContent, loading, quickContext, setQuickContext, contentPurpose, setContentPurpose, availableOptions }} />}
        </AnimatePresence>
        
        {/* Results Display */}
        {(generatedContent || analysisResult) && (
          <ResultsDisplay 
            content={generatedContent}
            analysis={analysisResult}
            selectedVariation={selectedVariation}
            setSelectedVariation={setSelectedVariation}
            showPsychology={showPsychology}
            setShowPsychology={setShowPsychology}
            handleSaveContent={handleSaveContent}
          />
        )}
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <SaveModal
          show={showSaveModal}
          onClose={() => {
            setShowSaveModal(false);
            setSelectedVariationsToSave([]);
          }}
        />
      )}

      {/* Saved Content Modal */}
      {showSavedContent && (
        <SavedContentModal
          show={showSavedContent}
          onClose={() => {
            setShowSavedContent(false);
            setExpandedSavedId(null);
          }}
          savedContent={savedContent}
          onDelete={deleteSavedContent}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          item={deleteConfirm}
          onConfirm={deleteSavedContent}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}; 

// Smart Mode Component
const SmartMode = ({ userIntent, setUserIntent, quickContext, setQuickContext, generateSmartContent, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-slate-700/50"
  >
    <div className="space-y-6">
      <div>
        <label className="text-lg font-semibold text-white mb-3 block">
          What do you want to create? 
        </label>
        <textarea
          value={userIntent}
          onChange={(e) => setUserIntent(e.target.value)}
          placeholder="Examples:
- Instagram post about matcha benefits for busy women
- Facebook and TikTok posts selling my course to entrepreneurs  
- LinkedIn thought leadership post about AI trends
- Cold email to SaaS founders pitching my service
- Create Instagram, Facebook, and LinkedIn posts about my new product launch"
          className="w-full min-h-[150px] bg-slate-900/50 border border-slate-700 rounded-xl p-4 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
        />
        <p className="text-xs text-slate-400 mt-2">
          💡 Mention multiple platforms (Instagram + Facebook + TikTok) to get unique content for each!
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-300 mb-2 block">
            What's your product/service?
          </label>
          <input
            type="text"
            value={quickContext.business}
            onChange={(e) => setQuickContext({...quickContext, business: e.target.value})}
            placeholder="e.g., Matcha powder, SaaS tool, Coaching..."
            className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-slate-300 mb-2 block">
            Who's your target audience?
          </label>
          <input
            type="text"
            value={quickContext.audience}
            onChange={(e) => setQuickContext({...quickContext, audience: e.target.value})}
            placeholder="e.g., Busy women 25-45, B2B founders..."
            className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
          />
        </div>
      </div>
      
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={generateSmartContent}
        disabled={!userIntent || loading}
        className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
      >
        {loading ? (
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            AI Detecting Intent & Generating Platform-Native Content...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <span>Generate Content</span>
            <span className="text-xl">🚀</span>
          </div>
        )}
      </motion.button>
    </div>
  </motion.div>
);

// 🆕 UPDATED: Specific Mode Component with Multi-Platform Selection
const SpecificMode = ({ selectedContentType, setSelectedContentType, selectedPlatforms, setSelectedPlatforms, specificFormData, setSpecificFormData, generateSpecificContent, loading, availableOptions }) => {
  // Content categories remain the same as in your document
  const contentCategories = {
    'Quick Wins': [
      { key: 'social_post', name: 'Social Post', platforms: true },
      { key: 'cold_email', name: 'Cold Email' },
      { key: 'product_description', name: 'Product Description' },
      { key: 'poll_question', name: 'Poll/Survey' },
      { key: 'stories_content', name: 'Stories Content' }
    ],
    
    'Sales & Conversion': [
      { key: 'sales_page', name: 'Sales Page' },
      { key: 'vsl_script', name: 'VSL Script' },
      { key: 'landing_page', name: 'Landing Page' },
      { key: 'webinar_registration', name: 'Webinar Page' },
      { key: 'upsell_copy', name: 'Upsell/Cross-sell' }
    ],
    
    'Email Marketing': [
      { key: 'cold_email', name: 'Cold Email' },
      { key: 'nurture_email', name: 'Nurture Email' },
      { key: 'sales_email', name: 'Sales Email' },
      { key: 'cart_abandonment', name: 'Cart Recovery' }
    ],
    
    'Short-Form Video': [
      { key: 'reels_script', name: 'Instagram Reels' },
      { key: 'shorts_script', name: 'YouTube Shorts' },
      { key: 'reels_script', name: 'TikTok Video', platform: 'tiktok' },
      { key: 'stories_content', name: 'Stories/Snaps' }
    ],
    
    'Visual Content': [
      { key: 'carousel_post', name: 'Carousel Post' },
      { key: 'linkedin_carousel', name: 'LinkedIn Carousel' }
    ],
    
    'Long-Form Content': [
      { key: 'blog_post', name: 'Blog Post' },
      { key: 'white_paper', name: 'White Paper' },
      { key: 'case_study', name: 'Case Study' },
      { key: 'thought_leadership', name: 'Thought Leadership' }
    ],
    
    'Video & Audio': [
      { key: 'youtube_script', name: 'YouTube Video' },
      { key: 'podcast_script', name: 'Podcast Episode' },
      { key: 'host_read_ad', name: 'Podcast Ad' },
      { key: 'spotify_ad', name: 'Spotify Ad' },
      { key: 'clubhouse_script', name: 'Audio Room' }
    ],
    
    'Social Platforms': [
      { key: 'social_post', name: 'Instagram', platform: 'instagram' },
      { key: 'social_post', name: 'Facebook', platform: 'facebook' },
      { key: 'social_post', name: 'TikTok', platform: 'tiktok' },
      { key: 'social_post', name: 'LinkedIn', platform: 'linkedin' },
      { key: 'social_post', name: 'Twitter/X', platform: 'twitter' },
      { key: 'reddit_post', name: 'Reddit' },
      { key: 'quora_answer', name: 'Quora' }
    ],
    
    'Paid Advertising': [
      { key: 'facebook_ad', name: 'Facebook Ad' },
      { key: 'instagram_ad', name: 'Instagram Ad' },
      { key: 'tiktok_ad', name: 'TikTok Ad' },
      { key: 'google_ad', name: 'Google Ad' },
      { key: 'youtube_ad', name: 'YouTube Ad' },
      { key: 'linkedin_ad', name: 'LinkedIn Ad' },
      { key: 'twitter_ad', name: 'Twitter Ad' },
      { key: 'reddit_ad', name: 'Reddit Ad' }
    ],
    
    'E-Commerce': [
      { key: 'product_description', name: 'Product Page' },
      { key: 'amazon_listing', name: 'Amazon Listing' },
      { key: 'collection_description', name: 'Collection Page' },
      { key: 'upsell_copy', name: 'Upsells' }
    ],
    
    'B2B Marketing': [
      { key: 'case_study', name: 'Case Study' },
      { key: 'white_paper', name: 'White Paper' },
      { key: 'comparison_page', name: 'Vs Competitor' },
      { key: 'linkedin_carousel', name: 'LinkedIn Carousel' }
    ],
    
    'SEO & Discovery': [
      { key: 'meta_description', name: 'Meta Descriptions' },
      { key: 'featured_snippet', name: 'Featured Snippets' },
      { key: 'faq_content', name: 'FAQ Content' }
    ],
    
    'Messaging & Chat': [
      { key: 'chatbot_script', name: 'Chatbot' },
      { key: 'whatsapp_broadcast', name: 'WhatsApp' },
      { key: 'sms_campaign', name: 'SMS Marketing' },
      { key: 'push_notification', name: 'Push Notifications' }
    ],
    
    'Interactive': [
      { key: 'quiz_content', name: 'Quiz Funnel' },
      { key: 'calculator_copy', name: 'Calculator Tool' },
      { key: 'poll_question', name: 'Polls' },
      { key: 'lead_magnet', name: 'Lead Magnet' }
    ],
    
    'Community': [
      { key: 'reddit_post', name: 'Reddit Post' },
      { key: 'discord_announcement', name: 'Discord' },
      { key: 'quora_answer', name: 'Quora Answer' }
    ],
    
    'Reviews & UGC': [
      { key: 'review_response', name: 'Review Responses' },
      { key: 'ugc_brief', name: 'UGC Brief' },
      { key: 'influencer_brief', name: 'Influencer Brief' }
    ],
    
    'PR & Media': [
      { key: 'press_release', name: 'Press Release' },
      { key: 'media_pitch', name: 'Media Pitch' },
      { key: 'thought_leadership', name: 'Op-Ed Article' }
    ]
  };

  // Platform options for social content
  const platformOptions = [
    { key: 'instagram', name: 'Instagram', icon: '📷' },
    { key: 'facebook', name: 'Facebook', icon: '👥' },
    { key: 'tiktok', name: 'TikTok', icon: '🎵' },
    { key: 'linkedin', name: 'LinkedIn', icon: '💼' },
    { key: 'twitter', name: 'Twitter/X', icon: '🐦' }
  ];
  
  const handleContentTypeSelect = (type) => {
    setSelectedContentType(type);
    // Reset platform selection when content type changes
    if (type === 'social_post') {
      setSelectedPlatforms([]);
    }
  };
  
  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Content Type Selection */}
      <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
        <h3 className="text-lg font-semibold text-white mb-4">Choose Content Type (50+ Options)</h3>
        
        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
          {Object.entries(contentCategories).map(([category, types]) => (
            <div key={category}>
              <h4 className="text-sm font-medium text-cyan-400 mb-2 sticky top-0 bg-slate-800/50 backdrop-blur-sm py-1 z-10">{category}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {types.map((item) => {
                  const isSelected = selectedContentType === item.key;
                  
                  return (
                    <button
                      key={`${item.key}-${item.platform || 'default'}`}
                      onClick={() => handleContentTypeSelect(item.key)}
                      className={`
                        p-3 rounded-lg text-sm font-medium transition-all border
                        ${isSelected
                          ? 'bg-cyan-600/20 border-cyan-500/50 text-white shadow-lg shadow-cyan-500/20' 
                          : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700/50 hover:border-slate-600'
                        }
                      `}
                    >
                      {item.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* 🆕 NEW: Platform Selection for Social Content */}
      {selectedContentType === 'social_post' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gradient-to-br from-purple-600/10 to-pink-600/10 backdrop-blur-xl rounded-2xl p-6 border border-purple-600/20"
        >
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <span>📱</span>
            <span>Select Platform(s)</span>
          </h3>
          <p className="text-sm text-slate-300 mb-4">
            Choose one or multiple platforms. Each will get platform-native optimization.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {platformOptions.map(platform => (
              <button
                key={platform.key}
                onClick={() => togglePlatform(platform.key)}
                className={`
                  p-4 rounded-xl transition-all border-2 flex flex-col items-center gap-2
                  ${selectedPlatforms.includes(platform.key)
                    ? 'bg-purple-600/30 border-purple-500 shadow-lg shadow-purple-500/20'
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }
                `}
              >
                <span className="text-2xl">{platform.icon}</span>
                <span className={`text-sm font-medium ${
                  selectedPlatforms.includes(platform.key) ? 'text-white' : 'text-slate-300'
                }`}>
                  {platform.name}
                </span>
                {selectedPlatforms.includes(platform.key) && (
                  <span className="text-xs text-purple-300">✓ Selected</span>
                )}
              </button>
            ))}
          </div>
          
          {selectedPlatforms.length > 0 && (
            <div className="mt-4 p-3 bg-purple-600/10 border border-purple-600/20 rounded-lg">
              <p className="text-sm text-purple-300">
                ✨ Will generate <span className="font-bold">{selectedPlatforms.length}</span> platform-native variation{selectedPlatforms.length > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </motion.div>
      )}
      
      {/* Form Fields */}
      {selectedContentType && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 space-y-4"
        >
          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">
              Business Context
            </label>
            <textarea
              value={specificFormData.business_context.description || ''}
              onChange={(e) => setSpecificFormData({
                ...specificFormData,
                business_context: { ...specificFormData.business_context, description: e.target.value }
              })}
              placeholder="Describe your product/service, unique value prop, pricing..."
              className="w-full min-h-[100px] bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Target Audience
              </label>
              <input
                type="text"
                value={specificFormData.target_audience}
                onChange={(e) => setSpecificFormData({...specificFormData, target_audience: e.target.value})}
                placeholder="e.g., Women 25-45, B2B founders"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Primary Goal
              </label>
              <input
                type="text"
                value={specificFormData.goal}
                onChange={(e) => setSpecificFormData({...specificFormData, goal: e.target.value})}
                placeholder="e.g., Drive sales, Get signups"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">
              Tone
            </label>
            <select
              value={specificFormData.tone}
              onChange={(e) => setSpecificFormData({...specificFormData, tone: e.target.value})}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50"
            >
              {(availableOptions?.tones || [
                'conversational',
                'professional',
                'casual',
                'urgent',
                'friendly',
                'authoritative',
                'empathetic',
                'bold',
                'humorous'
              ]).map(tone => (
                <option key={tone} value={tone}>
                  {tone.charAt(0).toUpperCase() + tone.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          {/* 🆕 UPDATED: Generate Button with validation */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={generateSpecificContent}
            disabled={
              loading || 
              !specificFormData.target_audience || 
              !specificFormData.goal ||
              (selectedContentType === 'social_post' && selectedPlatforms.length === 0)
            }
            className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-cyan-600/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Generating...' : 
             selectedContentType === 'social_post' && selectedPlatforms.length > 1 
               ? `Generate for ${selectedPlatforms.length} Platforms` 
               : 'Generate Content'}
          </motion.button>
          
          {/* Validation message */}
          {selectedContentType === 'social_post' && selectedPlatforms.length === 0 && (
            <p className="text-sm text-orange-400 text-center">
              Please select at least one platform above
            </p>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

// Analyze Mode
const AnalyzeMode = ({ existingContent, setExistingContent, analyzeContent, loading, quickContext, setQuickContext, contentPurpose, setContentPurpose, availableOptions }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-4 md:space-y-6"
    >
      <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-slate-700/50">
        <label className="text-base md:text-lg font-semibold text-white mb-3 block">
          What is this content for? 📝
        </label>
        <input
          type="text"
          value={contentPurpose}
          onChange={(e) => setContentPurpose(e.target.value)}
          placeholder="e.g., Instagram post about matcha, Facebook ad for course launch, Cold email to B2B clients..."
          className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-3 md:px-4 py-3 text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
        />
        <p className="text-xs text-slate-400 mt-2">
          💡 Be specific! Include the platform and purpose so we can analyze it with platform-specific intelligence.
        </p>
      </div>

      <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-slate-700/50 space-y-4">
        <div>
          <label className="text-base md:text-lg font-semibold text-white mb-3 block">
            Paste your content for analysis
          </label>
          <textarea
            value={existingContent}
            onChange={(e) => setExistingContent(e.target.value)}
            placeholder="Paste your ad copy, social post, email, landing page text, sales letter, or any marketing content..."
            className="w-full min-h-[200px] bg-slate-900/50 border border-slate-700 rounded-xl p-3 md:p-4 text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="text-xs md:text-sm font-medium text-slate-300 mb-2 block">
              Product/Service (optional)
            </label>
            <input
              type="text"
              value={quickContext.business}
              onChange={(e) => setQuickContext({...quickContext, business: e.target.value})}
              placeholder="e.g., SaaS tool, Online course"
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 md:px-4 py-2 md:py-3 text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50"
            />
          </div>
          <div>
            <label className="text-xs md:text-sm font-medium text-slate-300 mb-2 block">
              Target Audience (optional)
            </label>
            <input
              type="text"
              value={quickContext.audience}
              onChange={(e) => setQuickContext({...quickContext, audience: e.target.value})}
              placeholder="e.g., B2B founders, Fitness enthusiasts"
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 md:px-4 py-2 md:py-3 text-sm md:text-base text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50"
            />
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={analyzeContent}
          disabled={!existingContent || !contentPurpose || loading}
          className="w-full py-3 md:py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-sm md:text-base font-semibold rounded-xl shadow-lg shadow-teal-600/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {loading ? (
            <div className="flex items-center justify-center gap-3">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Analyzing with Structure Intelligence...
            </div>
          ) : (
            'Analyze & Generate Better Versions'
          )}
        </motion.button>
        
        {(!contentPurpose || !existingContent) && (
          <p className="text-xs text-slate-400 text-center">
            {!contentPurpose && !existingContent 
              ? 'Tell us what the content is for and paste your content to begin analysis'
              : !contentPurpose 
              ? 'Please describe what this content is for (include platform)'
              : 'Please paste your content to analyze'}
          </p>
        )}
      </div>
    </motion.div>
  );
};

// 🆕 COMPLETE UPDATED Results Display Component with Dynamic Structure Rendering
const ResultsDisplay = ({ content, analysis, selectedVariation, setSelectedVariation, showPsychology, setShowPsychology, handleSaveContent }) => {
  if (!content && !analysis) return null;
  
  const currentVariation = content?.variations?.[selectedVariation];
  
  // 🎨 Helper function to render content based on structure
  const renderContent = (variation) => {
    if (!variation?.content) return null;
    
    const contentData = variation.content;
    
    // CASE 1: Simple full_text (social posts, most content)
    if (contentData.full_text && !contentData.subject_line && !contentData.slides && !contentData.headline) {
      return (
        <div className="bg-slate-900/50 rounded-lg p-3 md:p-4 mb-4 border border-slate-700">
          <pre className="whitespace-pre-wrap text-sm md:text-base text-white font-sans break-words">
            {contentData.full_text}
          </pre>
        </div>
      );
    }
    
    // CASE 2: Email structure (subject, preview, body, ps)
    if (contentData.subject_line) {
      return (
        <div className="space-y-3 mb-4">
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <div className="text-xs font-medium text-blue-400 mb-1">📧 SUBJECT LINE</div>
            <div className="text-white font-semibold">{contentData.subject_line}</div>
          </div>
          
          {contentData.preview_text && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="text-xs font-medium text-cyan-400 mb-1">👁️ PREVIEW TEXT</div>
              <div className="text-slate-300">{contentData.preview_text}</div>
            </div>
          )}
          
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <div className="text-xs font-medium text-green-400 mb-2">✉️ EMAIL BODY</div>
            <pre className="whitespace-pre-wrap text-sm text-white font-sans">
              {contentData.body}
            </pre>
          </div>
          
          {contentData.ps && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="text-xs font-medium text-purple-400 mb-1">📝 P.S.</div>
              <div className="text-slate-300 italic">{contentData.ps}</div>
            </div>
          )}
        </div>
      );
    }
    
    // CASE 3: Slide-based content (carousel, stories)
    if (contentData.slides && Array.isArray(contentData.slides)) {
      return (
        <div className="space-y-3 mb-4">
          {contentData.slides.map((slide, index) => (
            <div key={index} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-purple-400">
                  📄 SLIDE {slide.slide_number || index + 1}
                </div>
                {slide.visual_note && (
                  <div className="text-xs text-slate-500">🎨 {slide.visual_note}</div>
                )}
              </div>
              {slide.headline && (
                <div className="text-white font-bold mb-2">{slide.headline}</div>
              )}
              {slide.body && (
                <div className="text-slate-300">{slide.body}</div>
              )}
            </div>
          ))}
        </div>
      );
    }
    
    // CASE 4: Long-form with sections (sales page, landing page)
    if (contentData.headline || contentData.body_sections) {
      return (
        <div className="space-y-3 mb-4">
          {contentData.headline && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="text-xs font-medium text-yellow-400 mb-1">🎯 HEADLINE</div>
              <div className="text-white text-xl font-bold">{contentData.headline}</div>
            </div>
          )}
          
          {contentData.subheadline && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="text-xs font-medium text-blue-400 mb-1">📝 SUBHEADLINE</div>
              <div className="text-slate-300 text-lg">{contentData.subheadline}</div>
            </div>
          )}
          
          {contentData.body_sections && typeof contentData.body_sections === 'object' && (
            <div className="space-y-3">
              {Object.entries(contentData.body_sections).map(([key, value]) => value && (
                <div key={key} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <div className="text-xs font-medium text-cyan-400 mb-2 uppercase">
                    {key.replace(/_/g, ' ')}
                  </div>
                  <pre className="whitespace-pre-wrap text-sm text-white font-sans">
                    {value}
                  </pre>
                </div>
              ))}
            </div>
          )}
          
          {contentData.full_text && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="text-xs font-medium text-green-400 mb-2">📄 COMPLETE VERSION</div>
              <pre className="whitespace-pre-wrap text-sm text-white font-sans">
                {contentData.full_text}
              </pre>
            </div>
          )}
        </div>
      );
    }
    
    // FALLBACK: Show whatever we have
    return (
      <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
        <pre className="whitespace-pre-wrap text-sm text-white font-sans">
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </div>
    );
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 space-y-6"
    >
      {/* 🆕 Platform/Type Detection Badges */}
      {content && (
        <div className="flex items-center gap-2 flex-wrap">
          {content.platforms && content.platforms.length > 0 && (
            <div className="px-4 py-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg">
              <span className="text-sm text-purple-300 font-medium">
                📱 Platform{content.platforms.length > 1 ? 's' : ''}: {' '}
                <span className="text-white font-semibold capitalize">
                  {content.platforms.join(', ').replace(/_/g, ' ')}
                </span>
              </span>
            </div>
          )}
          <div className="px-4 py-2 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 rounded-lg">
            <span className="text-sm text-cyan-300 font-medium">
              📄 Type: <span className="text-white font-semibold capitalize">{content.content_type?.replace(/_/g, ' ')}</span>
            </span>
          </div>
          {content.multi_platform && (
            <div className="px-4 py-2 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-lg">
              <span className="text-sm text-green-300 font-medium">
                ✨ Multi-Platform Generation
              </span>
            </div>
          )}
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
          <h3 className="text-xl font-semibold text-white mb-4">Analysis Results</h3>
          
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
              <div className="text-3xl font-bold text-blue-400">{analysis.overall_score || 0}%</div>
              <div className="text-sm text-slate-400 mt-1">Overall Score</div>
            </div>
            {analysis.platform_score && (
              <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
                <div className="text-3xl font-bold text-purple-400">{analysis.platform_score}%</div>
                <div className="text-sm text-slate-400 mt-1">Platform Fit</div>
              </div>
            )}
            {analysis.structure_score && (
              <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
                <div className="text-3xl font-bold text-cyan-400">{analysis.structure_score}%</div>
                <div className="text-sm text-slate-400 mt-1">Structure</div>
              </div>
            )}
            <div className="bg-slate-900/50 rounded-lg p-4 text-center border border-slate-700">
              <div className="text-3xl font-bold text-green-400">
                +{content?.variations?.[0]?.score_improvement || 0}%
              </div>
              <div className="text-sm text-slate-400 mt-1">Expected Improvement</div>
            </div>
          </div>
          
          {/* Strengths & Weaknesses */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-green-400 mb-2">✓ Strengths</h4>
              <ul className="space-y-1">
                {analysis.strengths?.map((strength, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-medium text-red-400 mb-2">× Areas to Improve</h4>
              <ul className="space-y-1">
                {analysis.weaknesses?.map((weakness, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-red-400 mt-0.5">×</span>
                    <span>{weakness}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Platform-Specific Issues */}
          {analysis.platform_specific_issues && analysis.platform_specific_issues.length > 0 && (
            <div className="mt-4 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg">
              <h4 className="text-sm font-medium text-purple-400 mb-2">📱 Platform-Specific Issues</h4>
              <ul className="space-y-1">
                {analysis.platform_specific_issues.map((issue, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">•</span>
                    <span>{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* 🆕 UPDATED: Variation Selector with Platform Badges */}
      {content?.variations && content.variations.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {content.variations.map((variation, index) => (
            <button
              key={index}
              onClick={() => setSelectedVariation(index)}
              className={`
                px-4 py-2 rounded-lg whitespace-nowrap transition-all flex-shrink-0 border
                ${selectedVariation === index 
                  ? 'bg-blue-600/20 border-blue-500/50 text-white shadow-lg shadow-blue-500/20' 
                  : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <div className="font-medium">
                  Version {variation.version || index + 1}
                  {variation.platform_name && (
                    <span className="ml-1 text-xs">
                      ({variation.platform_name})
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs mt-1">
                {variation.predicted_metrics?.conversion_rate || variation.predicted_score || 0}% predicted
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Content Display */}
      <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-slate-700/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg md:text-xl font-semibold text-white">
            Generated Content
            {currentVariation?.platform_name && (
              <span className="ml-2 text-sm font-normal text-purple-400">
                for {currentVariation.platform_name}
              </span>
            )}
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleSaveContent()}
              className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-600/30 rounded-lg text-purple-300 text-xs md:text-sm font-medium transition-all"
            >
              💾 Save
            </button>
            <button
              onClick={() => {
                const textToCopy = currentVariation?.content?.full_text || 
                                   JSON.stringify(currentVariation?.content, null, 2);
                navigator.clipboard.writeText(textToCopy);
              }}
              className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/30 rounded-lg text-blue-300 text-xs md:text-sm font-medium transition-all"
            >
              📋 Copy
            </button>
            <button
              onClick={() => setShowPsychology(!showPsychology)}
              className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-600/30 rounded-lg text-cyan-300 text-xs md:text-sm font-medium transition-all whitespace-nowrap"
            >
              {showPsychology ? '🧠 Hide' : '🧠 Show'} Psychology
            </button>
          </div>
        </div>
        
        {/* 🆕 UPDATED: Dynamic Content Rendering */}
        {renderContent(currentVariation)}
        
        {/* Psychological Breakdown */}
        {showPsychology && currentVariation && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-6 pt-6 border-t border-slate-700 space-y-4"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-blue-400 mb-2">🎯 Primary Framework</h4>
                <p className="text-white">
                  {currentVariation.psychological_mechanics?.primary_framework || 'Mixed approach'}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-400 mb-2">💫 Emotional Journey</h4>
                <p className="text-white">
                  {currentVariation.psychological_mechanics?.emotional_journey || 'Progressive engagement'}
                </p>
              </div>
            </div>

            {/* Platform Psychology */}
            {currentVariation.psychological_mechanics?.platform_psychology && (
              <div>
                <h4 className="text-sm font-medium text-purple-400 mb-2">
                  📱 Why This Works on {currentVariation.platform_name || content?.platforms?.[0] || 'This Platform'}
                </h4>
                <p className="text-slate-300 text-sm">
                  {currentVariation.psychological_mechanics.platform_psychology}
                </p>
              </div>
            )}
            
            {/* Psychological Triggers */}
            <div>
              <h4 className="text-sm font-medium text-cyan-400 mb-2">🎭 Psychological Triggers Used</h4>
              <div className="flex flex-wrap gap-2">
                {currentVariation.psychological_mechanics?.triggers_activated?.map((trigger, i) => (
                  <span key={i} className="px-3 py-1 bg-cyan-600/20 border border-cyan-600/30 rounded-full text-cyan-300 text-sm">
                    {trigger}
                  </span>
                ))}
              </div>
            </div>

            {/* Platform Optimizations */}
            {currentVariation.platform_optimization?.platform_features_used && 
             currentVariation.platform_optimization.platform_features_used.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-400 mb-2">✨ Platform Features Applied</h4>
                <div className="flex flex-wrap gap-2">
                  {currentVariation.platform_optimization.platform_features_used.map((feature, i) => (
                    <span key={i} className="px-3 py-1 bg-green-600/20 border border-green-600/30 rounded-full text-green-300 text-sm">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Predicted Metrics */}
            {currentVariation.predicted_metrics && (
              <div>
                <h4 className="text-sm font-medium text-orange-400 mb-3">📊 Predicted Performance</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center border border-slate-700">
                    <div className="text-2xl font-bold text-green-400">
                      {currentVariation.predicted_metrics.conversion_rate || 0}%
                    </div>
                    <div className="text-xs text-slate-400">Conversion</div>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center border border-slate-700">
                    <div className="text-2xl font-bold text-blue-400">
                      {currentVariation.predicted_metrics.engagement_rate || 0}%
                    </div>
                    <div className="text-xs text-slate-400">Engagement</div>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center border border-slate-700">
                    <div className="text-2xl font-bold text-cyan-400">
                      {currentVariation.predicted_metrics.persuasion_score || 0}%
                    </div>
                    <div className="text-xs text-slate-400">Persuasion</div>
                  </div>
                  {currentVariation.predicted_metrics.platform_performance && (
                    <div className="bg-slate-900/50 rounded-lg p-3 text-center border border-slate-700">
                      <div className="text-2xl font-bold text-purple-400">
                        {currentVariation.predicted_metrics.platform_performance}%
                      </div>
                      <div className="text-xs text-slate-400">Platform Fit</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Platform Optimization Details */}
            {currentVariation.platform_optimization && (
              <div className="grid md:grid-cols-3 gap-4">
                {currentVariation.platform_optimization.character_count && (
                  <div className="bg-slate-900/30 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-xs text-slate-400 mb-1">Character Count</div>
                    <div className="text-lg font-semibold text-white">
                      {currentVariation.platform_optimization.character_count}
                    </div>
                  </div>
                )}
                {currentVariation.platform_optimization.reading_time && (
                  <div className="bg-slate-900/30 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-xs text-slate-400 mb-1">Reading Time</div>
                    <div className="text-lg font-semibold text-white">
                      {currentVariation.platform_optimization.reading_time}
                    </div>
                  </div>
                )}
                {currentVariation.platform_optimization.native_feel_score && (
                  <div className="bg-slate-900/30 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-xs text-slate-400 mb-1">Native Feel Score</div>
                    <div className="text-lg font-semibold text-purple-400">
                      {currentVariation.platform_optimization.native_feel_score}%
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
      
      {/* Optimization Tips */}
      {content?.optimization_tips && content.optimization_tips.length > 0 && (
        <div className="bg-gradient-to-br from-blue-600/10 to-cyan-600/10 backdrop-blur-xl rounded-2xl p-6 border border-blue-600/20">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <span>💡</span>
            <span>AI Optimization Tips</span>
          </h3>
          <ul className="space-y-2">
            {content.optimization_tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                <span className="text-blue-400 mt-0.5">→</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* AI Decisions (Smart Mode) */}
      {content?.ai_decisions && (
        <div className="bg-gradient-to-br from-purple-600/10 to-pink-600/10 backdrop-blur-xl rounded-2xl p-6 border border-purple-600/20">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <span>🤖</span>
            <span>AI Strategic Decisions</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-purple-400 font-medium mb-1">Detected Type</div>
              <div className="text-white capitalize">{content.ai_decisions.content_type?.replace(/_/g, ' ')}</div>
            </div>
            {content.ai_decisions.platforms && content.ai_decisions.platforms.length > 0 && (
              <div>
                <div className="text-xs text-purple-400 font-medium mb-1">Platforms Detected</div>
                <div className="text-white capitalize">{content.ai_decisions.platforms.join(', ').replace(/_/g, ' ')}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-purple-400 font-medium mb-1">Intent</div>
              <div className="text-white capitalize">{content.ai_decisions.intent?.replace(/_/g, ' ')}</div>
            </div>
            <div>
              <div className="text-xs text-purple-400 font-medium mb-1">Psychological Approach</div>
              <div className="text-white capitalize">{content.ai_decisions.psychological_approach?.replace(/_/g, ' ')}</div>
            </div>
            {content.ai_decisions.cultural_context && content.ai_decisions.cultural_context !== 'general' && (
              <div className="md:col-span-2">
                <div className="text-xs text-purple-400 font-medium mb-1">Cultural Context</div>
                <div className="text-white">{content.ai_decisions.cultural_context}</div>
              </div>
            )}
            {content.ai_decisions.selling_strategy && (
              <div className="md:col-span-2">
                <div className="text-xs text-purple-400 font-medium mb-1">Selling Strategy</div>
                <div className="text-white capitalize">{content.ai_decisions.selling_strategy.replace(/_/g, ' ')}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ContentEngine;